import type { AuthClient, LoginRequest, Role, User } from '@auth/client';
import { isTokenExpired } from '@auth/client';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { AuthContextValue, PermissionAction } from '../types';

/**
 * ロール-アクション パーミッションマトリクス
 *
 * バックエンドの RBAC 設定と一致させる。
 * クライアントサイドはUI制御目的のみ（サーバーサイドでも二重チェック済み）。
 */
const ROLE_PERMISSIONS = {
  admin: new Set<PermissionAction>(['read', 'create', 'edit', 'delete', 'manage_schema']),
  employee: new Set<PermissionAction>(['read', 'create', 'edit', 'delete']),
  parttime: new Set<PermissionAction>(['read', 'create', 'edit']),
  contractor: new Set<PermissionAction>(['read']),
} as const satisfies Record<Role, ReadonlySet<PermissionAction>>;

/**
 * ロールとアクションに基づいてパーミッションを確認する純粋関数。
 *
 * @param role - 確認するユーザーロール（未認証の場合は null）
 * @param action - 確認するアクション
 * @returns 許可されている場合は true
 */
export function checkPermission(role: Role | null, action: PermissionAction): boolean {
  if (role === null) return false;
  return ROLE_PERMISSIONS[role].has(action);
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
  /**
   * テスト用 AuthClient。
   * 省略時はデフォルトのシングルトンが使用される。
   * 本番コードでは指定不要。
   */
  client: AuthClient;
}

function AuthProvider({ children, client }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(() => client.getCurrentUser());
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    async function initializeAuth(): Promise<void> {
      const currentUser = client.getCurrentUser();
      const accessToken = client.getAccessToken();

      // アクセストークンが存在し、JWT形式かつ期限切れの場合はリフレッシュを試行
      // isTokenExpired はデコード不能なトークンを期限切れとみなすため、
      // JWT形式（header.payload.signature）でない場合はスキップする
      const isJwtFormat = accessToken !== null && accessToken.split('.').length === 3;
      if (currentUser !== null && isJwtFormat && isTokenExpired(accessToken)) {
        // リフレッシュトークンが無い場合はリフレッシュ不可 → 静かにログアウト状態へ
        const refreshToken = client.getRefreshToken();
        if (refreshToken === null) {
          if (!cancelled) {
            setUser(null);
            setIsLoading(false);
          }
          return;
        }

        try {
          await client.refreshToken();
          if (!cancelled) {
            // リフレッシュ成功: 新しいユーザー情報で更新
            const refreshedUser = client.getCurrentUser();
            setUser(refreshedUser);
          }
        } catch {
          if (!cancelled) {
            // リフレッシュ失敗: ログアウト状態へ（エラートーストなし）
            setUser(null);
          }
        }
      } else {
        if (!cancelled) {
          setUser(currentUser);
        }
      }

      if (!cancelled) {
        setIsLoading(false);
      }
    }

    void initializeAuth();

    return () => {
      cancelled = true;
    };
  }, [client]);

  const login = useCallback(
    async (credentials: LoginRequest): Promise<void> => {
      const response = await client.login(credentials);
      setUser(response.user);
    },
    [client],
  );

  const logout = useCallback((): void => {
    void client.logout().finally(() => {
      setUser(null);
    });
  }, [client]);

  const role = user?.role ?? null;

  const hasPermission = useCallback(
    (action: PermissionAction): boolean => checkPermission(role, action),
    [role],
  );

  const updateUser = useCallback((newUser: User | null): void => {
    setUser(newUser);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      role,
      login,
      logout,
      hasPermission,
      updateUser,
    }),
    [user, isLoading, role, login, logout, hasPermission, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * 認証コンテキストを参照するカスタムフック。
 *
 * @throws AuthProvider の外部で使用した場合はエラーをスローする
 */
function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth は AuthProvider の内部で使用してください。');
  }
  return context;
}

export { AuthProvider, useAuth };
